import { FC } from "react";
import { Link } from "react-router-dom";
import { GapByCountyStatusResult, NetworkPortfolioManagementProject } from "@app/network-portfolio-management/interfaces";
import { ProjectDescriptionLine } from "@app/network-portfolio-management/components/ProjectDescriptionLine";
import { aliasLabel } from "@app/network-portfolio-management/utils/projectTypeUtils";
import { formatDateCustom } from "@app/shared/formatDateTime";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESWidget } from "@app/shared/widget/QESWidget";
import { QESWidgetContent } from "@app/shared/widget/QESWidgetContent";
import { QESWidgetFooter } from "@app/shared/widget/QESWidgetFooter";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { ProjectRunConfigurationType } from "@app/projects/enumerators";
import { useMembershipToggle } from "@app/network-portfolio-management/hooks/useMembershipToggle";
import { ProjectStatusDisplay } from "@app/network-portfolio-management/components/ProjectStatusDisplay";
import { ProjectTagList } from "@app/network-portfolio-management/components/ProjectTagList";
import { ArchivedDatePill } from "@app/network-portfolio-management/components/ArchivedDatePill";
import { ProjectCardMetrics } from "@app/network-portfolio-management/components/ProjectCardMetrics";
import { useNpmActionMenuProps } from "@app/network-portfolio-management/hooks/useNpmActionMenuProps";
import { SharedProjectActionMenu } from "@app/projects/network-assessment/SharedProjectActionMenu";

interface ProjectCardProps {
  project: NetworkPortfolioManagementProject;
  isSelected: boolean;
  onToggleSelect: (id: number) => void;
  /** When false the selection checkbox is hidden entirely (no Project Action filter is active). */
  selectionEnabled: boolean;
  /** Gap by County Status for this project, loaded independently — see ProjectInfiniteList. */
  gapByCountyStatus?: GapByCountyStatusResult;
  isGapByCountyStatusLoading?: boolean;
  gapByCountyStatusHasError?: boolean;
}

const formatLastRunDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) {
    return null;
  }
  return formatDateCustom(dateString, "MMM D, YYYY h:mm A");
};

export const ProjectCard: FC<ProjectCardProps> = ({
  project,
  isSelected,
  onToggleSelect,
  selectionEnabled,
  gapByCountyStatus,
  isGapByCountyStatusLoading = false,
  gapByCountyStatusHasError = false,
}) => {
  // Derive the archived layout from the project itself (not the dashboard view) so the card stays a reusable
  // presentational component — an archived project is simply one with an archived date.
  const isArchived = project.dateArchived != null;
  // The archived view shows a reduced card: no selection, status, gap, adequacy/accuracy, or
  // membership controls — just identity, the archived-date pill, geography metadata, and tags (AC #5).
  const { active, membership, setMembership, showMembershipToggle } = useMembershipToggle(project);
  const actionMenuProps = useNpmActionMenuProps(project);
  // Same primary/secondary toggle as `active`, applied to the independently-loaded gap breakdown.
  const isSecondarySelected = showMembershipToggle && membership === ProjectRunConfigurationType.DualMembershipSupplemental;
  const selectedGapByCountyStatus = isSecondarySelected ? gapByCountyStatus?.secondary : gapByCountyStatus?.primary;
  const gapByCountyStatusErrored = gapByCountyStatusHasError || !!gapByCountyStatus?.hasError;

  const formattedDate = formatLastRunDate(project.lastModifiedDate);

  return (
    <div data-testid={`network-portfolio-management-project-card-${project.projectId}`}>
      <QESWidget className="network-portfolio-management-project-card">
        <QESWidgetContent>
          <div className="network-portfolio-management-project-card__header">
            {selectionEnabled && (
              <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN]}>
                <div className="network-portfolio-management-project-card__checkbox">
                  <QESCheckbox
                    checked={isSelected}
                    label={`Select ${project.name}`}
                    testId={`network-portfolio-management-card-checkbox-${project.projectId}`}
                    onChange={() => onToggleSelect(project.projectId)}
                  />
                </div>
              </RoleRestricted>
            )}
            <ArchivedDatePill
              dateArchived={project.dateArchived}
              testId={`network-portfolio-management-card-archived-pill-${project.projectId}`}
            />
            {!isArchived && project.status && (
              <span data-testid={`network-portfolio-management-card-status-${project.projectId}`}>
                <ProjectStatusDisplay project={project} />
              </span>
            )}
            <div className="network-portfolio-management-project-card__actions">
              <SharedProjectActionMenu {...actionMenuProps} />
            </div>
          </div>

          {showMembershipToggle && (
            <QESSelect
              containerClassName="network-portfolio-management-project-card__membership"
              hideLabel
              label="Membership"
              className="network-portfolio-management-project-card__membership-select"
              testId={`network-portfolio-management-card-membership-${project.projectId}`}
              value={membership}
              onChange={(e) => setMembership(e.target.value as ProjectRunConfigurationType)}
            >
              <option value={ProjectRunConfigurationType.DualMembershipPrimary}>
                {aliasLabel(project.primary?.memberSetAlias, "Primary")}
              </option>
              <option value={ProjectRunConfigurationType.DualMembershipSupplemental}>
                {aliasLabel(project.secondary?.memberSetAlias, "Secondary")}
              </option>
            </QESSelect>
          )}

          <TooltipWrapper text={project.name} triggerWidth="100%">
            <Link
              to={`/projects/${project.projectId}`}
              className="network-portfolio-management-project-card__name"
              data-testid={`network-portfolio-management-card-go-to-project-${project.projectId}`}
            >
              {project.name}
            </Link>
          </TooltipWrapper>
          <ProjectDescriptionLine
            lineOfBusiness={project.lineOfBusiness}
            projectType={project.projectType}
            countyCount={active?.countyCount}
            zipCodeCount={active?.zipCodeCount}
            templateName={project.templateName}
            testId={`network-portfolio-management-card-lob-${project.projectId}`}
          />

          <ProjectCardMetrics
            project={project}
            selectedMembership={active}
            gapByCountyStatus={selectedGapByCountyStatus}
            isGapByCountyStatusLoading={isGapByCountyStatusLoading}
            gapByCountyStatusHasError={gapByCountyStatusErrored}
          />
        </QESWidgetContent>

        <QESWidgetFooter className="network-portfolio-management-project-card__footer">
          <ProjectTagList
            tags={project.tags}
            className="network-portfolio-management-project-card__tags"
            itemClassName="network-portfolio-management-project-card__tag"
            testId={`network-portfolio-management-card-tags-${project.projectId}`}
          />
          {!!formattedDate && (
            <span className="network-portfolio-management-project-card__date" data-testid={`network-portfolio-management-card-date-${project.projectId}`}>
              Last Run On: {formattedDate}
            </span>
          )}
        </QESWidgetFooter>
      </QESWidget>
    </div>
  );
};
